import { CircularProgress } from '@mui/material'
import {
  InsertChartOutlined as ChartIcon,
  ErrorOutline as ErrorIcon,
  SearchOff as SearchOffIcon,
} from '@mui/icons-material'

export const truncateText = (text: string, maxLength: number): string => {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

export type ThemeUIState = {
  postLoading: boolean
  getLoading: boolean
  getError: string | null
  postError: string | null
  isExtracting: boolean
  postSuccess: boolean
  getSuccess?: boolean
  hasNoThemes: boolean
  initialLoadDone?: boolean
}

// Get appropriate message based on current state
export const getMessage = ({
  postLoading,
  getLoading,
  getError,
  postError,
  isExtracting,
  postSuccess,
  getSuccess,
  hasNoThemes,
  initialLoadDone,
}: ThemeUIState) => {
  if (postLoading) {
    return 'AI is analyzing your contributions to identify common themes and key phrases. This may take a few moments...'
  }
  if (getLoading) {
    return 'Retrieving themes from your contributions...'
  }
  if (getError) {
    return 'Unable to load themes. Please try again later.'
  }
  if (postError) {
    return 'Unable to extract themes. Please try again later.'
  }
  if (isExtracting && hasNoThemes && postSuccess) {
    return 'Themes are being extracted in the background. Click "Reload Themes" to check if extraction is complete.'
  }
  if (hasNoThemes && getSuccess && initialLoadDone) {
    return 'No themes found. Click the "Extract Themes" button above to analyze your contributions and generate AI-powered themes and key phrases.'
  }
  if (hasNoThemes) {
    return 'Click the "Extract Themes" button above to analyze your contributions and generate AI-powered themes and key phrases.'
  }
  return ''
}

// Get appropriate title based on current state
export const getTitle = ({
  postLoading,
  getLoading,
  getError,
  postError,
  isExtracting,
  postSuccess,
  hasNoThemes,
}: ThemeUIState) => {
  if (postLoading) return 'Extracting themes'
  if (getLoading) return 'Loading themes'
  if (getError) return 'Failed to load themes'
  if (postError) return 'Failed to extract themes'
  if (isExtracting && hasNoThemes && postSuccess)
    return 'No themes identified yet'
  if (hasNoThemes) return 'No themes available'

  return ''
}

const iconSx = { mb: 1, fontSize: 60, color: 'grey.300' }
const spinnerSx = { mb: 2, color: 'grey.300' }

// Get appropriate icon based on current state
export const getIcon = ({
  postLoading,
  getLoading,
  getError,
  postError,
  isExtracting,
  postSuccess,
  hasNoThemes,
}: ThemeUIState): JSX.Element => {
  if (postLoading || getLoading) return <CircularProgress sx={spinnerSx} />
  if (getError || postError) return <ErrorIcon sx={iconSx} />
  if (isExtracting && hasNoThemes && postSuccess)
    return <SearchOffIcon sx={iconSx} />

  return <ChartIcon sx={iconSx} />
}

export const getTooltipTitle = (
  getLoading: boolean,
  noThemes: boolean,
): string => {
  if (getLoading) return 'Extracting Themes'
  if (noThemes) return 'No themes available'
  return ''
}
